import { useEffect, useMemo } from 'react';

import {
	type BaseAIAction,
	type BaseAISmartButtonContext,
	useAISmartButtonActions,
} from '../../ai-smart-button-store';

/**
 * Hook for managing actions associated with a specific source.
 * Automatically handles cleanup on unmount.
 */
export const useSourceAIActions = (sourceId: string) => {
	const { removeContext, removeAll, setActions, setNudges, setSuggestedActions, setContext } =
		useAISmartButtonActions();

	useEffect(() => {
		// Cleanup on unmount.
		return () => {
			removeAll(sourceId);
			removeContext(sourceId);
		};
	}, [sourceId, removeAll, removeContext]);

	return useMemo(
		() => ({
			/**
			 * Replaces all actions for this source with new ones.
			 */
			setActions: (actions: BaseAIAction[]) => {
				setActions(sourceId, actions);
			},

			/*
			 * Replaces all nudges for this source with new ones.
			 */
			setNudges: (nudges: BaseAIAction[]) => {
				setNudges(sourceId, nudges);
			},

			/**
			 * Replaces all suggested actions for this source with new ones.
			 */
			setSuggestedActions: (suggestedActions: BaseAIAction[]) => {
				setSuggestedActions(sourceId, suggestedActions);
			},

			/**
			 * Sets context for this source.
			 */
			setContext: (context: BaseAISmartButtonContext) => setContext({ ...context, sourceId }),

			/**
			 * Removes context for this source.
			 */
			removeContext: () => removeContext(sourceId),

			/**
			 * Removes all actions for this source.
			 */
			removeAll: () => removeAll(sourceId),
		}),
		[sourceId, removeAll, removeContext, setActions, setNudges, setSuggestedActions, setContext],
	);
};

export type SourceAIActions = ReturnType<typeof useSourceAIActions>;
